import React, { useEffect } from "react";
import useLocalStorageState from "use-local-storage-state";
import WDButton from "./WDButton";
import { useAppDispatch, useAppSelector } from "../../state/hooks";
import {
  gameApiSliceActions,
  gameData,
  gameOrdersMeta,
  gameOverview,
  gameStatus,
  gameViewedPhase,
  saveOrders,
} from "../../state/game/game-api-slice";
import UpdateOrder from "../../interfaces/state/UpdateOrder";
import { RootState } from "../../state/store";
import { OrderStatus } from "../../interfaces/state/MemberData";
import OrderSubmission from "../../interfaces/state/OrderSubmission";
import useSettings from "../../hooks/useSettings";
import t from "../../i18n";

enum OrderStatusButton {
  SAVE = "save",
  READY = "ready",
}

interface WDOrderStatsControlsProps {
  orderStatus: OrderStatus;
}

const WDOrderStatusControls: React.FC<WDOrderStatsControlsProps> = function ({
  orderStatus,
}): React.ReactElement {
  const { settings } = useSettings();

  const overview = useAppSelector(gameOverview);
  const { data } = useAppSelector(gameData);
  const ordersMeta = useAppSelector(gameOrdersMeta);
  const status = useAppSelector(gameStatus);
  const viewedPhaseState = useAppSelector(gameViewedPhase);
  const savingOrdersInProgress = useAppSelector(
    (state) => state.game.savingOrdersInProgress,
  );

  const viewingCurPhase =
    viewedPhaseState.viewedPhaseIdx >= status.phases.length - 1;

  const currentOrderInProgress = useAppSelector(
    ({ game: { order } }: RootState) => order.inProgress,
  );

  const dispatch = useAppDispatch();

  const ordersMetaValues = Object.values(ordersMeta);
  const ordersLength = ordersMetaValues.length;
  const ordersSaved = ordersMetaValues.reduce(
    (acc, meta) => acc + +meta.saved,
    0,
  );

  let readyEnabled: boolean;
  let saveEnabled: boolean;
  let readyButtonText: string;
  let saveButtonText: string;
  const saveText = t("Save");
  const { user } = overview;
  const extraSCs = user ? user.member.supplyCenterNo - user.member.unitNo : 0;
  /*
   * 沙盒建造阶段：一个人控制所有国家，但界面上的 member 只是其中一个国家。
   * 当这个国家没有建造名额（orderStatus.None）、而别的国家还有没填的建造槽时，
   * 不能把整局当成「无需操作」——否则 Ready 永远是禁用的，服务端也就永远
   * 等不到就绪，Builds 阶段会一直卡在「结算中」。
   */
  const pendingSandboxBuilds =
    !!data?.isSandboxMode &&
    overview.phase === "Builds" &&
    (data.currentOrders || []).some(
      (o) =>
        !o.toTerrID && (o.type === "Build Army" || o.type === "Build Fleet"),
    );
  const canSave =
    ordersLength > 0 &&
    (overview.phase === "Diplomacy" ||
      ordersLength !== ordersSaved ||
      (overview.phase === "Builds" && extraSCs > 0));

  // orderStatus contains what the server thinks our order status is.
  if (savingOrdersInProgress === "readying") {
    readyEnabled = false;
    saveEnabled = false;
    readyButtonText = t("Readying...");
    saveButtonText = saveText;
  } else if (savingOrdersInProgress === "unreadying") {
    readyEnabled = false;
    saveEnabled = false;
    readyButtonText = t("Unreadying...");
    saveButtonText = saveText;
  } else if (savingOrdersInProgress === "saving") {
    readyEnabled = false;
    saveEnabled = false;
    readyButtonText = t("Ready");
    saveButtonText = t("Saving...");
  } else if (orderStatus.Ready) {
    readyEnabled = viewingCurPhase;
    saveEnabled = false;
    readyButtonText = t("Unready");
    saveButtonText = saveText;
  } else if (orderStatus.None && !pendingSandboxBuilds) {
    /*
     * 本轮没有命令可下（例如撤退阶段没有需要撤退的单位、建造阶段无处可建）：
     * 玩家无需任何操作，按钮一律禁用，交给服务端自动结算。
     *
     * 不能落到下面的 else 分支：那里用 ordersMeta（会残留上一阶段的订单）判断
     * 是否可操作，会把「无需操作」误判为可操作 —— 于是「就绪」按钮能点、还让
     * 玩家白等半天。
     */
    readyEnabled = false;
    saveEnabled = false;
    readyButtonText = t("Ready");
    saveButtonText = saveText;
  } else if (orderStatus.Saved || orderStatus.Completed) {
    readyEnabled = viewingCurPhase;
    saveEnabled = ordersLength !== ordersSaved && viewingCurPhase;
    readyButtonText = t("Ready");
    saveButtonText = saveText;
  } else {
    /*
     * 沙盒建造阶段别的国家还有没填的建造槽时，用户自己的国家可能没有任何
     * 订单（None），但依然要允许点 Ready：服务端会把空槽按 Wait 处理。
     */
    readyEnabled = viewingCurPhase && (canSave || pendingSandboxBuilds);
    saveEnabled = viewingCurPhase && canSave;
    readyButtonText = t("Ready");
    saveButtonText = saveText;
  }

  const doAnimateGlow =
    saveEnabled && ordersLength !== ordersSaved && !currentOrderInProgress;

  const clickButton = (whatButton: OrderStatusButton) => {
    // console.log("Entered save button click");
    // When you click save or ready, it should clear any actively entered order you have going,
    // and/or any of the move input flyover. It doesn't make sense to ready and have the UI
    // stay with a partially-entered order.
    dispatch(gameApiSliceActions.resetOrder());

    if ("currentOrders" in data && "contextVars" in data) {
      const { currentOrders, contextVars } = data;
      if (contextVars && currentOrders) {
        const orderUpdates: UpdateOrder[] = [];
        currentOrders.forEach(
          ({ fromTerrID, id, toTerrID, type: moveType, unitID, viaConvoy }) => {
            const updateReference = ordersMeta[id].update;
            let orderUpdate: UpdateOrder = {
              fromTerrID,
              id,
              toTerrID,
              type: moveType || "",
              unitID,
              viaConvoy,
            };
            if (updateReference) {
              orderUpdate = {
                ...orderUpdate,
                ...updateReference,
              };
            }
            orderUpdates.push(orderUpdate);
          },
        );
        const orderSubmission: OrderSubmission = {
          orderUpdates,
          context: JSON.stringify(contextVars.context),
          contextKey: contextVars.contextKey,
          queryParams: {},
          userIntent: "saving",
        };
        if (whatButton === OrderStatusButton.READY) {
          if (orderStatus.Ready) {
            orderSubmission.queryParams = { notready: "on" };
            orderSubmission.userIntent = "unreadying";
          } else {
            orderSubmission.queryParams = { ready: "on" };
            orderSubmission.userIntent = "readying";
          }
        }
        // console.log({ orderSubmission });
        dispatch(saveOrders(orderSubmission));
      }
    }
  };

  useEffect(() => {
    const needsToSave = Object.keys(ordersMeta).some(
      (key) => ordersMeta[key].saved === false,
    );
    if (needsToSave && doAnimateGlow && settings.autoSave) {
      clickButton(OrderStatusButton.SAVE);
    }
  }, [ordersMeta, settings]);

  const buttonClass = "w-14 h-14 rounded-full sm:w-fit sm:px-[30px]";

  /*
   * 正在查看历史阶段时，保存/就绪全被禁用（服务端只接受当前阶段的指令），
   * 而且自动保存也依赖 saveEnabled，于是表现成"点了没反应、服务器一条请求都收不到"，
   * 玩家会以为指令存不上。这里直接把原因和出口（跳回当前阶段）摆出来。
   */
  if (!viewingCurPhase) {
    return (
      <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-3 w-fit">
        <span className="text-xs opacity-80">
          {t(
            "Viewing an earlier phase - jump to the current phase to give orders",
          )}
        </span>
        <WDButton
          color="primary"
          className={buttonClass}
          onClick={() => dispatch(gameApiSliceActions.setViewedPhaseToLatest())}
        >
          {t("Jump to current phase")}
        </WDButton>
      </div>
    );
  }

  return (
    <div className="flex flex-col sm:flex-row justify-end space-y-2 space-x-0 sm:space-x-3 sm:space-y-0 w-fit">
      {!settings.autoSave && (
        <WDButton
          color="primary"
          className={buttonClass}
          disabled={!saveEnabled}
          onClick={() => saveEnabled && clickButton(OrderStatusButton.SAVE)}
          doAnimateGlow={doAnimateGlow}
        >
          {saveButtonText}
        </WDButton>
      )}
      <WDButton
        color="primary"
        className={buttonClass}
        disabled={!readyEnabled}
        onClick={() => readyEnabled && clickButton(OrderStatusButton.READY)}
      >
        {readyButtonText}
      </WDButton>
    </div>
  );
};

export default WDOrderStatusControls;
